import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button, Input } from '@afrilink/ui';
import { siteConfig, getPartnerToken } from '@afrilink/config';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';
import { PASSWORD_MIN_LENGTH, validatePassword } from '@/shared/utils/passwordPolicy';
import {
  AuthLayout,
  loadWizardPlan,
  type AuthVariant,
} from '../components';

/**
 * Honour `?return=<path>` so the order-hosting wizard (LD-O6) can route
 * unauthenticated visitors through sign-up and bounce them back to
 * /app/services/new with their wizard state intact (sessionStorage carries
 * the plan + fqdn-mode across the gate).
 *
 * Only same-origin paths are accepted to avoid open-redirect.
 */
function safeReturnPath(raw: string | null): string {
  if (!raw) return '/app';
  if (!raw.startsWith('/')) return '/app';
  if (raw.startsWith('//')) return '/app';
  return raw;
}

/**
 * Read UTM params from the current URL searchParams (M3.15) and shape into
 * the `acquisition_source` JSON we persist to customer_profile via the
 * on_auth_user_created trigger. Returns null if no utm_source present —
 * direct signups, staff invites, and import flows all skip this column.
 *
 * Marketing site CTAs tag with utm_source=marketing + utm_medium/campaign/
 * content (M3.14, see marketing/src/config/portal.ts getPortalUrl).
 */
const ACQUISITION_SESSION_KEY = 'afrilink_acquisition_source';

interface AcquisitionSource {
  source: string;
  medium?: string;
  campaign?: string;
  content?: string;
  captured_at: string;
}

function readAcquisitionSource(searchParams: URLSearchParams): AcquisitionSource | null {
  // Priority: URL params first (fresh entry), then sessionStorage (preserved
  // across auth redirects e.g. wizard → sign-up → wizard).
  const urlSource = searchParams.get('utm_source');
  if (urlSource) {
    const captured: AcquisitionSource = {
      source: urlSource,
      medium: searchParams.get('utm_medium') ?? undefined,
      campaign: searchParams.get('utm_campaign') ?? undefined,
      content: searchParams.get('utm_content') ?? undefined,
      captured_at: new Date().toISOString(),
    };
    // Persist so subsequent navigations (e.g. wizard → sign-up after sign-in)
    // can still recover it even if the URL has been cleaned.
    try {
      sessionStorage.setItem(ACQUISITION_SESSION_KEY, JSON.stringify(captured));
    } catch {
      // SessionStorage disabled / private mode — silently fall through.
    }
    return captured;
  }

  // Fallback: sessionStorage from a prior page load (likely a marketing CTA
  // landed on /app/services/new which redirected here for auth).
  try {
    const stored = sessionStorage.getItem(ACQUISITION_SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as AcquisitionSource;
      if (parsed.source) return parsed;
    }
  } catch {
    // Ignore parse / storage errors.
  }
  return null;
}

/**
 * SignUpPage — public self-signup. New customers create their portal account
 * here without staff intervention.
 *
 * Flow:
 *   1. User enters email + password (+ optional full name)
 *   2. supabase.auth.signUp() — Supabase sends confirmation email
 *   3. We show "Check your inbox" success state
 *   4. User clicks link in email → lands at /sign-in with #type=signup hash
 *      → SignInPage detects it and navigates to /app
 *
 * If the project has email-confirm OFF, signUp returns a session immediately
 * and we navigate straight to /app.
 *
 * If the email is already registered, Supabase silently returns success
 * (anti-enumeration) — the UI message handles both cases generically and
 * directs the user to "Forgot password?" if they already have an account.
 *
 * No magic links anywhere — sign-in is password only; recovery is via the
 * /sign-in "Forgot password?" link.
 *
 * Spec: _bmad-output/USER_LIFECYCLE_SCHEMA_AND_AUDIT.md §5
 */

type Mode = 'form' | 'sent';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const returnPath = safeReturnPath(searchParams.get('return'));

  const [mode, setMode] = useState<Mode>('form');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && isAuthenticated) {
    return <Navigate to={returnPath} replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Enter your email address.');
      return;
    }
    const pwError = validatePassword(password, confirmPassword);
    if (pwError) {
      setError(pwError);
      return;
    }

    setSubmitting(true);
    try {
      // After email confirmation, Supabase redirects to /sign-in. From there
      // SignInPage drops the user at /app (or honours `from` location state).
      // For LD-O6 (unauth → wizard) we encode the return path in the
      // emailRedirectTo so the verification link comes back to the wizard
      // route, preserving the user's flow.
      const verifyRedirect = returnPath !== '/app'
        ? `${window.location.origin}/sign-in?from=${encodeURIComponent(returnPath)}`
        : `${window.location.origin}/sign-in`;
      // Capture UTM attribution at sign-up (M3.15). The handle_new_user
      // trigger reads acquisition_source from raw_user_meta_data and writes
      // it to customer_profile. Null when there are no utm_* params (direct
      // signups, staff/import flows).
      const acquisitionSource = readAcquisitionSource(searchParams);
      // Partner attribution (2026-09-25). A visitor who arrived via /p/<token>
      // and signs up FIRST used to lose the partner entirely: this page ignored
      // the token, and the verification email opens in a NEW tab whose
      // sessionStorage is empty. The token therefore travels in the auth
      // metadata and handle_new_user resolves it server-side at account
      // creation — the one moment that survives the email round-trip.
      const partnerToken = getPartnerToken();
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: verifyRedirect,
          data: {
            full_name: fullName.trim() || email.trim(),
            source: 'self_signup',
            ...(acquisitionSource ? { acquisition_source: acquisitionSource } : {}),
            ...(partnerToken ? { partner_token: partnerToken } : {}),
          },
        },
      });
      if (signUpError) {
        setError(humaniseAuthError(signUpError.message));
        return;
      }
      // If email confirmation is OFF, signUp returns a session immediately.
      if (data.session) {
        navigate(returnPath, { replace: true });
        return;
      }
      // Otherwise, Supabase has sent a confirmation email — show success.
      setMode('sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create account.');
    } finally {
      setSubmitting(false);
    }
  }

  // Variant resolution — wizard-return when sessionStorage has an active
  // order in flight, marketing-direct otherwise. AuthLayout will re-call
  // loadWizardPlan() internally to render the plan recap card on desktop.
  // Spec: _bmad-output/AUTH_AND_TRUST_BRD_2026-05-17.md §5.3.
  const variant: AuthVariant =
    loadWizardPlan() !== null ? 'wizard-return' : 'marketing-direct';

  return (
    <AuthLayout variant={variant}>
      <header className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy-primary">
          {mode === 'form' ? 'Create your account' : 'Almost there'}
        </h1>
        {mode === 'form' && (
          <p className="mt-1 text-sm text-muted-foreground">
            Free to create — you'll only be charged for what you order.
          </p>
        )}
      </header>

      <div className="rounded-md border border-border bg-card p-6 shadow-card">
        {mode === 'sent' && (
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <p className="text-navy-primary">
                Check your inbox at <strong>{email}</strong>. Click the
                confirmation link to activate your account.
              </p>
            </div>
            <p className="text-muted-foreground">
              If you already have an account, the email may be a sign-in link
              instead. Either way, follow the link to enter the portal.
            </p>
            <Link to="/sign-in" className="block">
              <Button type="button" variant="outline" className="w-full">
                Back to sign in
              </Button>
            </Link>
          </div>
        )}

        {mode === 'form' && (
          <form onSubmit={onSubmit} className="space-y-4">
            <Field
              label="Full name"
              value={fullName}
              onChange={setFullName}
              type="text"
              autoComplete="name"
              disabled={submitting}
              autoFocus
              hint="Optional, but helps us personalise communication."
            />
            <Field
              label="Email"
              value={email}
              onChange={setEmail}
              type="email"
              autoComplete="email"
              disabled={submitting}
            />
            <Field
              label="Password"
              value={password}
              onChange={setPassword}
              type="password"
              autoComplete="new-password"
              disabled={submitting}
              hint="Minimum 10 characters. Mix upper, lower, and a digit."
            />
            <Field
              label="Confirm password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              type="password"
              autoComplete="new-password"
              disabled={submitting}
            />
            {error && <ErrorBanner message={error} />}
            <Button type="submit" variant="marketing" className="w-full text-xs" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? 'Creating account…' : 'Create account'}
            </Button>
            <div className="flex items-center justify-between text-xs">
              <Link
                to="/sign-in"
                className="text-muted-foreground hover:text-primary hover:underline"
              >
                Already have an account? Sign in
              </Link>
              <a
                href={siteConfig.url}
                className="text-muted-foreground hover:text-primary hover:underline"
              >
                ← Back to website
              </a>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}

// validatePassword now lives in @/shared/utils/passwordPolicy — it was
// duplicated byte-for-byte here and in SignInPage, while SettingsSecurityPage
// carried a weaker third copy (S-6, 2026-08-30).

function Field({
  label,
  value,
  onChange,
  type,
  autoComplete,
  disabled,
  autoFocus,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: string;
  autoComplete: string;
  disabled?: boolean;
  autoFocus?: boolean;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-navy-secondary">
        {label}
      </span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        disabled={disabled}
        autoFocus={autoFocus}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </label>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-md border border-danger/30 bg-danger/10 p-3 text-sm text-danger"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function humaniseAuthError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('already registered') || m.includes('already exists')) {
    return 'An account with this email may already exist. Try signing in or use "Forgot password?" instead.';
  }
  if (m.includes('weak') || m.includes('password')) {
    return `Password is too weak. Use at least ${PASSWORD_MIN_LENGTH} characters with mixed case and a digit.`;
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  if (m.includes('email') && m.includes('invalid')) {
    return 'That email address is not valid.';
  }
  return raw;
}
